import { createCompanyApi, updateCompanyApi } from "@/api/services/admin/company";
import { useProductContractHandoff } from "@/api/services/admin/product-contract";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { ConfirmResetData } from "@/features/company/components/ConfirmRestData";
import { companySchema } from "@/features/company/schema/companySchema";
import { TCompanyForm } from "@/features/company/types/companyForm";
import { convertCompanyFormToCreateReq, convertCompanyFormToUpdateReq } from "@/features/company/utils/converts";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { ECompanyContractStatus } from "@/types/enum";
import { CONTRACT_TOKEN_KEY } from "@/utils/constants";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { getCenterFrom, getDynamicRoute, redirectToCenter } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, SelectChangeEvent, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

type TCompanyFormProps = {
  previousData?: Partial<TCompanyForm>;
};

export const CompanyForm: FC<TCompanyFormProps> = ({ previousData }) => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const toast = useToast();
  const { contract } = useProductContractHandoff();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirmReset = useDialog();
  const [isSaved, setIsSaved] = useState(false);

  const methods = useForm<TCompanyForm>({
    defaultValues: previousData,
    resolver: yupResolver(companySchema),
    mode: "onBlur",
  });
  const { control, setValue } = methods;
  useTriggerValidate({ methods, name: "contractStatus", triggerFields: ["contractStartDate"] });
  useTriggerValidate({ methods, name: "contractStartDate", triggerFields: ["contractEndDate"] });

  // TODO: done company regular
  // const { data: checkResult } = useCheckToUpdateYearAndQuarter();
  useEffect(() => {
    const companyName = methods.getValues("companyName");
    if (!companyName && contract?.organizationName) {
      methods.resetField("companyName", { defaultValue: contract.organizationName });
    }
  }, [contract]);

  useEffect(() => {
    if (!isSaved) return;
    if (isEditMode) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, id] });
      navigate(getDynamicRoute(AppRoutes.companyDetail, { id }));
    } else {
      Cookies.remove(CONTRACT_TOKEN_KEY);
      // 遷移元のcenterパスが分かっていれば、その画面へそのまま戻す。
      const centerFrom = getCenterFrom();
      if (centerFrom) {
        redirectToCenter(centerFrom);
        return;
      }
      if (!contract) {
        const centerOrganizationList = "/organizations";
        redirectToCenter(centerOrganizationList);
        return;
      }
      const centerOrganizationDetail = getDynamicRoute("/organizations/:id", { id: contract.organizationId });
      redirectToCenter(centerOrganizationDetail);
    }
  }, [isSaved]);

  const onSubmit = async (data: TCompanyForm) => {
    try {
      if (isEditMode) {
        if (
          data.startReportYear.get("year") !== previousData?.startReportYear?.get("year") ||
          data.startReportQuarter !== previousData.startReportQuarter
        ) {
          const result = await confirmReset.show();
          if (!result) return;
        }
        const payload = convertCompanyFormToUpdateReq(data);
        const rs = await updateCompanyApi(id!, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        setIsSaved(true);
        return;
      }
      if (!contract) {
        navigate(AppRoutes.companyList);
        return;
      }
      const payload = convertCompanyFormToCreateReq(data, contract.organizationId, contract.productContractId);
      const rs = await createCompanyApi(payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setIsSaved(true);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const { isLoading: isLoadingCompanySpecificIndustry, companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();

  const trialEndDateWatchField = useWatch({ control, name: "trialEndDate" });
  const trialStartDateWatchField = useWatch({ control, name: "trialStartDate" });

  const contractEndDateWatchField = useWatch({ control, name: "contractEndDate" });
  const contractStartDateWatchField = useWatch({ control, name: "contractStartDate" });

  const handleChangeContractStatus = (event: SelectChangeEvent<unknown>) => {
    if (event.target.value === ECompanyContractStatus.TRIAL) {
      if (!trialStartDateWatchField) {
        methods.setValue("trialStartDate", dayjs());
      }
      if (!trialEndDateWatchField) {
        const newDate = dayjs(trialStartDateWatchField || dayjs()).add(2, "weeks");
        setValue("trialEndDate", newDate);
      }
    }

    if (event.target.value === ECompanyContractStatus.DURING_CONTRACT && !contractStartDateWatchField) {
      methods.setValue("contractStartDate", dayjs());
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={companySchema}>
      <Box width="814px" mx="auto">
        <InnerBox>
          <Grid container rowSpacing={2} columnSpacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name="companyName">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="specificIndustryId">
                <Select
                  loading={isLoadingCompanySpecificIndustry}
                  readonly={previousData?.specificBusinessClassificationIds && previousData.specificBusinessClassificationIds.length > 0}
                  options={companySpecificIndustryOptions}
                />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="contractStatus">
                <Select enumName="ECompanyContractStatus" onChange={handleChangeContractStatus} />
              </FormField>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <FormField control={control} name="trialStartDate">
                <InputDatePicker
                  maxDate={trialEndDateWatchField ?? undefined}
                  onChange={(value) => {
                    if (!trialEndDateWatchField && !!value) {
                      const newDate = dayjs(value).add(2, "weeks");
                      setValue("trialEndDate", newDate);
                    }
                  }}
                />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="trialEndDate">
                <InputDatePicker minDate={trialStartDateWatchField ?? undefined} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField
                required={methods.watch("contractStatus") === ECompanyContractStatus.DURING_CONTRACT.toString()}
                control={control}
                name="contractStartDate"
              >
                <InputDatePicker maxDate={contractEndDateWatchField ?? undefined} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="contractEndDate">
                <InputDatePicker minDate={contractStartDateWatchField ?? undefined} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="startReportYear">
                <InputDatePicker format="YYYY" views={["year"]} readonly={!!previousData} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="startReportQuarter">
                <Select enumName="EQuarter" readonly={!!previousData} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="pricePerEmployee">
                <Input endAdornment="円/人" />
              </FormField>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <FormField control={control} name="companyDescription">
                <Textarea />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="note">
                <Textarea />
              </FormField>
            </Grid>
          </Grid>
        </InnerBox>
        <FooterActionBar mt={4}>
          <Stack width="100%" justifyContent="space-between" flexDirection="row">
            {isEditMode ? (
              <Button
                variant="ghost"
                size="sm"
                component={Link}
                to={isEditMode ? getDynamicRoute(AppRoutes.companyDetail, { id }) : AppRoutes.companyList}
              >
                戻る
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" size="sm">
              {isEditMode ? "保存" : "追加"}
            </Button>
          </Stack>
        </FooterActionBar>
      </Box>
      <ConfirmResetData open={confirmReset.open} onClose={confirmReset.cancel} onOk={confirmReset.confirm} />
    </Form>
  );
};
